<template>
   <div>
      <div v-if="!props.loading" class="chart-container">
         <Line
            id="chart"
            ref="line"
            :options="chartOptions"
            :data="props.chartData!"
            :height="props.height"
         />
      </div>
      <QSkeleton v-else type="rect" :height="props.height" width="100%" />
   </div>
</template>

<script setup lang="ts">
import {
   ref,
   Ref,
   onMounted,
   onUnmounted,
   VNodeRef,
   computed,
   PropType,
   ComputedRef,
} from "vue"
import { Line } from "vue-chartjs"
import { enUS } from "date-fns/locale"

import {
   Chart as ChartJS,
   Tooltip,
   LineElement,
   ChartData,
   PointElement,
   TimeScale,
   LinearScale,
   Filler,
   Legend,
   Title,
   ChartOptions,
} from "chart.js"

ChartJS.register(
   Tooltip,
   LineElement,
   TimeScale,
   LinearScale,
   PointElement,
   Filler,
   Legend,
   Title,
)

import "chartjs-adapter-date-fns"

const props = defineProps({
   showLegend: {
      type: Boolean,
      required: true,
   },
   height: {
      type: String,
      required: true,
   },
   chartData: {
      type: Object as PropType<ChartData<"line">>,
      required: true,
   },
   loading: {
      type: Boolean,
      required: true,
   },
})

const tooltipEnabled = ref(true)
const max: Ref<number | undefined> = ref(100)

const chartOptions: ComputedRef<ChartOptions<"line">> = computed(() => ({
   // noinspection TypeScriptValidateTypes
   responsive: true,
   interaction: {
      mode: props.showLegend ? "index" : "nearest",
      intersect: false,
   },
   elements: {
      line: {
         tension: 0.4,
      },
   },
   scales: {
      y: {
         beginAtZero: true,
         stacked: true,
         max: 100, // Since we're now always dealing with percentages
         position: "right",
         grid: {
            display: true,
            lineWidth: (context) => {
               const length = context.scale.ticks.length
               if (context.index === 0 || context.index === length - 1) {
                  return 0
               }
               return 1
            },
         },
         ticks: {
            // Format as percentage with 1 decimal place
            callback: function (tickValue: number | string) {
               const value =
                  typeof tickValue === "string"
                     ? parseFloat(tickValue)
                     : tickValue
               return Number.isFinite(value) ? value.toFixed() + "%" : ""
            },
         },
      },
      x: {
         display: false,
         grid: {
            display: false,
         },
         type: "time",
         time: {
            // unit: "hour",
            round: "hour",
            // tooltipFormat: "MMM d HH:00",
         },
         adapters: {
            date: {
               locale: enUS,
            },
         },
         ticks: {
            source: "auto",
            autoSkip: true,
            autoSkipPadding: 75,
            maxRotation: 0,
         },
      },
   },
   plugins: {
      filler: {
         drawTime: "beforeDatasetsDraw",
         propagate: true,
      },
      title: {
         display: true,
         text: "Traffic Distribution",
         font: {
            size: 18,
         },
      },
      legend: {
         position: "top",
         display: props.showLegend,
         onClick: () => {
            // Don't allow filtering
            return
         },
         onHover: function (event, legendItem, legend) {
            max.value = undefined
            tooltipEnabled.value = false
            legend.chart.data.datasets.forEach((e, i) => {
               if (i === legendItem.datasetIndex) {
                  e.hidden = false
               } else {
                  e.hidden = true
               }
            })
            legend.chart.update()
         },
         onLeave: function (event, legendItem, legend) {
            max.value = 100
            tooltipEnabled.value = true
            legend.chart.data.datasets.forEach((e) => {
               e.hidden = false
            })
            legend.chart.update()
         },
      },
      tooltip: {
         enabled: tooltipEnabled.value,
         padding: 10,
         caretPadding: 10,
         itemSort: (a, b) => (b.raw as number) - (a.raw as number),
         callbacks: {
            title: (context) => {
               const date = new Date(context[0].parsed.x)
               const options = <Intl.DateTimeFormatOptions>{
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  timeZoneName: "short",
               }
               return date.toLocaleString(undefined, options)
            },
            label: function (context) {
               let label = context.dataset.label || ""

               if (label) {
                  label += ": "
               }
               if (context.parsed.y !== null) {
                  label += Math.abs(context.parsed.y).toFixed(2) + "%"
               }
               return label
            },
            beforeBody: () => {
               return "Traffic Distribution:"
            },
         },
      },
   },
}))

const line: VNodeRef = ref(null)

const handleResize = () => {
   if (!line.value) return
   line.value.chart.resize()
}

onMounted(() => {
   window.addEventListener("resize", handleResize)
})

onUnmounted(() => {
   window.removeEventListener("resize", handleResize)
})
</script>
<style lang="scss">
.chart-container {
   position: relative;
   height: 100%;
   width: 100%;
}
#chart {
   position: relative;
   height: 100%;
   width: 100%;
}
</style>
